"use client";

import React, { useState, useEffect } from 'react';
import { MERResult, Grade } from '../types';
import { type ScoreTriple, getGradeScore } from '../calculator/calculator';
import { appGetItem, appSetItem } from "@/lib/auth-storage";
import { JupasProgram } from '../types';
import { getUniversityKey } from '../common';
import { INSTITUTIONS_DATA } from '../calculator/data';
import { formatElectiveRequirementLabel, formatGerElectiveRequirementLabel, isIncludeSubjectsRequirement } from '../lib/utils/gerElectiveLabels';

// Chevron icons as inline SVG components
const ChevronUpIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Props for the entrance requirements components
 */
interface EntranceRequirementsProps {
  ger?: MERResult & { requirements?: { [subject: string]: string | number } };
  per?: MERResult & { requirements?: { [subject: string]: string | number } };
  userScores?: Record<string, ScoreTriple>;
  selectedScoreType?: 'min' | 'current' | 'max';
  allowedCategories?: ('A' | 'B' | 'C')[];
  program?: JupasProgram;
}

const minimumLevelColumnClassName = 'grid-cols-[minmax(0,1fr)_9rem]';
const minimumLevelCellClassName = 'grid w-full grid-cols-[minmax(0,1fr)_1.25rem] items-center justify-end gap-2 text-gray-900 text-sm font-medium';
const electiveSubjectsHeaderClassName = 'font-semibold text-gray-800 text-sm pr-6';
const electiveMinimumLevelHeaderClassName = 'font-semibold text-gray-800 text-sm text-right pr-3';
const electiveMinimumLevelCellClassName = `${minimumLevelCellClassName} pr-3`;

const getInstitutionElectivesMinLevel = (program?: JupasProgram): number | undefined => {
  if (!program) return undefined;
  const instKey = getUniversityKey(program.jupas_id);
  const institutionData = instKey ? (INSTITUTIONS_DATA as any)[instKey] : undefined;
  const minLevel = institutionData?.general_mer?.electives_min_level;
  return typeof minLevel === 'number' ? minLevel : undefined;
};

const getProgrammeElectiveMinLevelFallback = (program?: JupasProgram): number | undefined => {
  if (!program?.program_mer) return undefined;

  const levels: number[] = [];
  const programMer = program.program_mer;

  if (typeof programMer.electives?.min_level === 'number') {
    levels.push(programMer.electives.min_level);
  }

  if (typeof programMer.one_of_electives?.min_level === 'number') {
    levels.push(programMer.one_of_electives.min_level);
  }

  if (Array.isArray(programMer.one_of_groups)) {
    programMer.one_of_groups.forEach(group => {
      if (typeof group.min_level === 'number') {
        levels.push(group.min_level);
      }
    });
  }

  if (typeof programMer.additional_any_one_subject?.min_level === 'number') {
    levels.push(programMer.additional_any_one_subject.min_level);
  }

  if (typeof programMer.additional_any_two_subjects?.min_level === 'number') {
    levels.push(programMer.additional_any_two_subjects.min_level);
  }

  if (programMer.elective_minima) {
    Object.values(programMer.elective_minima).forEach(group => {
      Object.values(group).forEach(level => {
        if (typeof level === 'number') {
          levels.push(level);
        }
      });
    });
  }

  return levels.length > 0 ? Math.max(...levels) : undefined;
};

/**
 * General Entrance Requirements component - shows core subject requirements (3322+A)
 */
export const GeneralEntranceRequirements: React.FC<EntranceRequirementsProps> = ({
  ger,
  userScores,
  selectedScoreType = 'current',
  allowedCategories = ['A', 'B', 'C'],
  program
}) => {
  // Expand/collapse state in the in-memory app store.
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = appGetItem('collapsible_ger');
      return stored !== null ? stored === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      appSetItem('collapsible_ger', String(isExpanded));
    }
  }, [isExpanded]);

  if (!ger) return null;
  
  const allCoresMet = Object.values(ger.core_minima).every(v => v);
  const electiveRequirements = Array.isArray(ger.elective_requirements) ? ger.elective_requirements : [];
  const electiveAlternatives = Array.isArray(ger.elective_alternatives) ? ger.elective_alternatives : [];
  const electivesMet = electiveAlternatives.length > 0
    ? electiveAlternatives.some(path => path.every(requirement => requirement.met))
    : electiveRequirements.length > 0
      ? electiveRequirements.every(requirement => requirement.met)
    : (ger.electives.required === 0 || ger.electives.met);
  const allGerMet = ger.eligible ?? (allCoresMet && electivesMet);

  // Get electives info from ger
  const electivesRequired = ger.electives.required;
  const electivesMinLevel = ger.electives.min_level ?? getInstitutionElectivesMinLevel(program) ?? 2;

  // Check if there are programme-specific elective requirements  
  const hasSpecificElectiveReqs = ger.specific_electives || ger.one_of_electives;

  // Get core subjects
  const coreSubjects = Object.entries(ger.core_minima);

// debugger;
  

  return (
    <div className="rounded-lg overflow-hidden border border-gray-300 shadow-md bg-gray-50">
      {/* Header */}
      <div 
        className="flex items-center justify-between bg-blue-50 px-4 py-3 border-b border-gray-300 cursor-pointer select-none"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
          General Entrance Requirements
        </h3>
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-6 h-6 rounded-full ${allGerMet ? 'bg-green-500' : 'bg-red-600'}`}>
            <span className="text-white text-sm font-bold">
              {allGerMet ? '✓' : '✗'}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
            className="p-1 rounded-md hover:bg-blue-100 transition-colors duration-200 text-gray-600"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </div>
      </div>

      {/* Collapsible Table Container */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-white">

        {/* Core Subjects Table */}
        <div className="border border-gray-300">
          <div className={`bg-gray-200 px-4 py-3 grid ${minimumLevelColumnClassName} border-b border-gray-300`}>
            <div className="font-semibold text-gray-800 text-sm">Core Subjects</div>
            <div className={electiveMinimumLevelHeaderClassName}>Minimum Level</div>
          </div>
          <div>
            {coreSubjects.map(([subject, met]) => {
              // Get minimum level from ger.requirements
              const minLevel = ger.requirements?.[subject] ||
                (subject === 'Citizenship and Social Development' ? 'Attained' : 3);

              // Convert 'A' to 'Attained' for display
              const displayMinLevel = (minLevel === 'A' || minLevel === 'a') ? 'Attained' : minLevel;

              return (
                <div key={subject} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                  <div className="text-gray-900 text-sm">{subject}</div>
                  <div className={minimumLevelCellClassName}>
                      <span className="text-right">{displayMinLevel}</span>
                      <span className={`text-sm font-bold w-5 text-center ${met ? 'text-green-600' : 'text-red-600'}`}>
                      {met ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Elective Subjects Table */}
        <div className="border border-gray-300">
          <div className={`bg-gray-200 px-4 py-3 grid ${minimumLevelColumnClassName} border-b border-gray-300`}>
            <div className={electiveSubjectsHeaderClassName}>Elective Subject(s)</div>
            <div className="font-semibold text-gray-800 text-sm text-right">Minimum Level</div>
          </div>
          <div>
            {/* Prefer institution elective_requirements (e.g. HKBU Cat A excl. M1/M2 + Cat A) */}
            {electiveAlternatives.length > 0 ? (
              electiveAlternatives.map((path, pathIndex) => (
                <div key={`ger-elective-alternative-${pathIndex}`}>
                  {pathIndex > 0 && (
                    <div className="px-4 py-2 bg-gray-100 text-center text-sm font-semibold text-gray-700">Or</div>
                  )}
                  {path.map((requirement, requirementIndex) => (
                    <div key={`ger-elective-alternative-${pathIndex}-${requirementIndex}`} className={`px-4 py-3 grid ${minimumLevelColumnClassName} bg-white`}>
                      <div className="text-gray-900 text-sm">{formatElectiveRequirementLabel(requirement)}</div>
                      <div className={electiveMinimumLevelCellClassName}>
                        <span className="text-right">{requirement.min_level ?? electivesMinLevel}</span>
                        <span className={`text-sm font-bold w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                          {requirement.met ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : electiveRequirements.length > 0 ? (
              electiveRequirements.map((requirement, index) => (
                <div key={`ger-elective-req-${index}`} className={`px-4 py-3 grid ${minimumLevelColumnClassName} bg-white`}>
                  <div className="text-gray-900 text-sm">
                    {formatGerElectiveRequirementLabel(requirement)}
                  </div>
                  <div className={electiveMinimumLevelCellClassName}>
                    <span className="text-right">{requirement.min_level ?? electivesMinLevel}</span>
                    <span className={`text-sm font-bold w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                      {requirement.met ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              ))
            ) : electivesRequired > 0 ? (
              <div className={`px-4 py-3 grid ${minimumLevelColumnClassName} bg-white`}>
                <div className="text-gray-900 text-sm">
                  Any {electivesRequired === 1 ? 'one' : electivesRequired === 2 ? 'two' : electivesRequired} {electivesRequired === 1 ? 'subject' : 'subjects'}
                  {allowedCategories.length < 3 && (
                    <span className="text-gray-600 ml-1">
                      (in Cat. {allowedCategories.join(', ')})
                    </span>
                  )}
                </div>
                <div className={electiveMinimumLevelCellClassName}>
                    <span className="text-right">{electivesMinLevel}</span>
                    <span className={`text-sm font-bold w-5 text-center ${ger.electives.met ? 'text-green-600' : 'text-red-600'}`}>
                    {ger.electives.met ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            ) : null}

            {/* Placeholder text when programme has specific requirements */}
            {/* {hasSpecificElectiveReqs && (
              <div className="px-4 py-3 bg-white text-gray-500 text-sm italic">
                See Programme Entrance Requirements
              </div>
            )} */}
          </div>
        </div>

      </div>
      </div>
    </div>
  );
};

/**
 * Programme Entrance Requirements component - shows programme-specific requirements
 */
export const ProgrammeEntranceRequirements: React.FC<EntranceRequirementsProps> = ({
  per,
  userScores,
  selectedScoreType = 'current',
  allowedCategories = ['A', 'B', 'C'],
  program
}) => {
  // Expand/collapse state in the in-memory app store.
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = appGetItem('collapsible_per');
      return stored !== null ? stored === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      appSetItem('collapsible_per', String(isExpanded));
    }
  }, [isExpanded]);

  if (!per) return null;
  
  // Check if there are any programme-specific requirements
  const electiveAlternatives = Array.isArray(per.elective_alternatives) ? per.elective_alternatives : [];
  const hasElectiveAlternatives = electiveAlternatives.length > 0;
  const hasElectiveRequirements = Array.isArray(per.elective_requirements) && per.elective_requirements.length > 0;
  const hasSpecialPerDisplay = !hasElectiveRequirements && (per.one_of_groups || per.one_of_electives);
  const hasRequirements = hasElectiveAlternatives || hasElectiveRequirements || hasSpecialPerDisplay || per.specific_electives || per.electives.required > 0;

  if (!hasRequirements) {
    return null;
  }

  // Calculate if all programme requirements are met
  const allProgrammeReqsMet =
    per.eligible ??
    (
      Object.values(per.core_minima).every(v => v) &&
      (!per.elective_requirements || per.elective_requirements.every(req => req.met)) &&
      (!hasElectiveAlternatives || electiveAlternatives.some(path => path.every(req => req.met))) &&
      (!per.one_of_groups || per.one_of_groups.every((g: any) => g.met)) &&
      (!per.one_of_electives || per.one_of_electives.met) &&
      (!per.specific_electives || Object.values(per.specific_electives).every((req: any) => req.met)) &&
      (!per.additional_any_one_subject || per.additional_any_one_subject.met) &&
      (!per.additional_any_two_subjects || per.additional_any_two_subjects.met)
    );

  // Get core subjects from per.core_minima
  const coreSubjects = Object.entries(per.core_minima);

  return (
    <div className="rounded-lg overflow-hidden border border-gray-300 shadow-md bg-gray-50">
      {/* Header */}
      <div 
        className="flex items-center justify-between bg-purple-50 px-4 py-3 border-b border-gray-300 cursor-pointer select-none"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
          Programme Entrance Requirements
          {/* {score_system && (
            <span className="ml-2 text-indigo-700">
              ({score_system === '8.5_SCALE' ? '8.5 Scale' : '7 Scale'})
            </span>
          )} */}
        </h3>
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-6 h-6 rounded-full ${allProgrammeReqsMet ? 'bg-green-500' : 'bg-red-600'}`}>
            <span className="text-white text-sm font-bold">
              {allProgrammeReqsMet ? '✓' : '✗'}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
            className="p-1 rounded-md hover:bg-purple-100 transition-colors duration-200 text-gray-600"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </div>
      </div>

      {/* Collapsible Table Container */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-white">

        {/* Core Subjects Table */}
        <div className="border border-gray-300">
          <div className={`bg-gray-200 px-4 py-3 grid ${minimumLevelColumnClassName} border-b border-gray-300`}>
            <div className="font-semibold text-gray-800 text-sm">Core Subjects</div>
            <div className={electiveMinimumLevelHeaderClassName}>Minimum Level</div>
          </div>
          <div>
            {coreSubjects.map(([subject, met]) => {
              // Get the minimum level from per.requirements prop or use default
              const minLevel = per?.requirements?.[subject] !== undefined ?
                per.requirements[subject] :
                (subject === 'Citizenship and Social Development' ? 'Attained' : 3);

              // Convert 'A' to 'Attained' for display
              const displayMinLevel = (minLevel === 'A' || minLevel === 'a') ? 'Attained' : minLevel;

              return (
                <div key={subject} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                  <div className="text-gray-900 text-sm">{subject}</div>
                  <div className={minimumLevelCellClassName}>
                      <span className="text-right">{displayMinLevel}</span>
                      <span className={`text-sm font-bold w-5 text-center ${met ? 'text-green-600' : 'text-red-600'}`}>
                      {met ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Elective Subjects Table */}
        <div className="border border-gray-300">
          <div className={`bg-gray-200 px-4 py-3 grid ${minimumLevelColumnClassName} border-b border-gray-300`}>
            <div className={electiveSubjectsHeaderClassName}>Elective Subject(s)</div>
            <div className="font-semibold text-gray-800 text-sm text-right">Minimum Level</div>
          </div>
          <div>
            {hasElectiveAlternatives && electiveAlternatives.map((path, pathIndex) => (
              <React.Fragment key={`per-elective-alternative-${pathIndex}`}>
                {pathIndex > 0 && (
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-100 text-gray-900 text-sm font-bold">
                    Or
                  </div>
                )}
                {path.map((requirement, requirementIndex) => (
                  <div
                    key={`per-elective-alternative-${pathIndex}-${requirementIndex}`}
                    className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}
                  >
                    <div className="text-gray-900 text-sm">
                      {formatElectiveRequirementLabel(requirement)}
                    </div>
                    <div className={electiveMinimumLevelCellClassName}>
                      <span className="text-right">{requirement.min_level ?? 3}</span>
                      <span className={`text-sm font-bold w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                        {requirement.met ? '✓' : '✗'}
                      </span>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}

            {/* Primary source: elective_requirements (non-include_subjects rows rendered FIRST, above special displays) */}
            {!hasElectiveAlternatives && hasElectiveRequirements && !hasSpecialPerDisplay && [...per.elective_requirements!]
              .filter(r => !isIncludeSubjectsRequirement(r))
              .map((requirement, index) => {
              const minLevel = requirement.min_level ?? 3;
              return (
                <div key={`er-any-${index}`} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                  <div className="text-gray-900 text-sm">{formatGerElectiveRequirementLabel(requirement)}</div>
                  <div className={electiveMinimumLevelCellClassName}>
                      <span className="text-right">{minLevel}</span>
                      <span className={`text-sm font-bold w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                      {requirement.met ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Special programme display: additional_any_one_subject (e.g. JS4238) - placed ABOVE one_of sections */}
            {!hasElectiveAlternatives && per.additional_any_one_subject && !hasElectiveRequirements && (
              <div className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} bg-white hover:bg-gray-50 transition-colors`}>
                <div className="text-gray-900 text-sm">Any one subject</div>
                <div className={electiveMinimumLevelCellClassName}>
                    <span className="text-right">{per.additional_any_one_subject.min_level}</span>
                    <span className={`text-sm font-bold w-5 text-center ${per.additional_any_one_subject.met ? 'text-green-600' : 'text-red-600'}`}>
                    {per.additional_any_one_subject.met ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            )}

            {/* Special programme display: one_of_groups (e.g. JS1202) */}
            {!hasElectiveAlternatives && !hasElectiveRequirements && per.one_of_groups && per.one_of_groups.map((group, groupIndex) => (
              <React.Fragment key={`group-${groupIndex}`}>
                <div className="px-4 py-2.5 border-b border-blue-300 col-span-2 bg-blue-100">
                  <div className="text-blue-900 text-sm font-bold flex items-center gap-2 flex-wrap">
                    <span>{group.name || `One of the following (Group ${groupIndex + 1}):`}</span>
                  </div>
                </div>
                {group.subjects.map((subject) => {
                  const minLevel = group.min_level;
                  let subjectMet: boolean | null = null;
                  let hasSubject = false;

                  if (userScores && userScores[subject]) {
                    const scoreTriple = userScores[subject];
                    const scoreTypeKey = selectedScoreType || 'current';
                    const userGrade = scoreTriple[scoreTypeKey] as Grade;
                    if (userGrade && (userGrade as string) !== '-') {
                      hasSubject = true;
                      const userLevel = program ? getGradeScore(userGrade as string, program) : 0;
                      subjectMet = userLevel >= (typeof minLevel === 'number' ? minLevel : parseInt(String(minLevel)));
                    }
                  }

                  return (
                    <div key={subject} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                      <div className="text-gray-900 text-sm">{subject}</div>
                      <div className={electiveMinimumLevelCellClassName}>
                          <span className="text-right">{minLevel}</span>
                          <span className={`text-sm font-bold w-5 text-center ${hasSubject && subjectMet ? 'text-green-600' : ''}`}>
                          {hasSubject && subjectMet ? '✓' : ' '}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {/* Special programme display: one_of_electives (e.g. JS4238) */}
            {!hasElectiveAlternatives && !hasElectiveRequirements && per.one_of_electives && !per.one_of_groups && (
              <>
                {per.one_of_electives.subjects.length > 1 && (
                  <div className="px-4 py-2.5 border-b border-blue-300 col-span-2 bg-blue-100">
                    <div className="text-blue-900 text-sm font-bold flex items-center gap-2 flex-wrap">
                      <span>{per.one_of_electives.requireCount === 2 ? 'Two of the following elective subjects' : 'One of the following elective subjects'}</span>
                    </div>
                  </div>
                )}
                {per.one_of_electives.subjects.map((subject) => {
                  const minLevel = per.one_of_electives?.min_level || 3;
                  let subjectMet: boolean | null = null;
                  let hasSubject = false;

                  if (userScores && userScores[subject]) {
                    const scoreTriple = userScores[subject];
                    const scoreTypeKey = selectedScoreType || 'current';
                    const userGrade = scoreTriple[scoreTypeKey] as Grade;
                    if (userGrade && (userGrade as string) !== '-') {
                      hasSubject = true;
                      const userLevel = program ? getGradeScore(userGrade as string, program) : 0;
                      subjectMet = userLevel >= (typeof minLevel === 'number' ? minLevel : parseInt(String(minLevel)));
                    }
                  }

                  return (
                    <div key={subject} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                      <div className="text-gray-900 text-sm">{subject}</div>
                      <div className={electiveMinimumLevelCellClassName}>
                          <span className="text-right">{minLevel}</span>
                          <span className={`text-sm font-bold w-5 text-center ${hasSubject && subjectMet ? 'text-green-600' : ''}`}>
                          {hasSubject && subjectMet ? '✓' : ' '}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Special programme display: additional_any_one_subject - old position removed, now above */}

            {/* Primary source: elective_requirements (include_subjects rows rendered as "One of" sections) */}
            {!hasElectiveAlternatives && hasElectiveRequirements && !hasSpecialPerDisplay && [...per.elective_requirements!]
              .filter(r => isIncludeSubjectsRequirement(r))
              .map((requirement, index) => {
              const minLevel = requirement.min_level ?? 3;
              const reqCount = requirement.requireCount || 1;
              const subjects = requirement.include_subjects!;
              return (
                <React.Fragment key={`er-inc-${index}`}>
                  <div className="px-4 py-2.5 border-b border-blue-300 bg-blue-100">
                    <div className="text-blue-900 text-sm font-bold">
                      {reqCount === 2 ? 'Two of the following elective subjects' : 'One of the following elective subjects'}
                    </div>
                  </div>
                  {subjects.map(subject => {
                    const isMatched = requirement.matched_subjects?.includes(subject);
                    return (
                      <div key={subject} className={`px-4 py-3 border-b border-gray-200 grid ${minimumLevelColumnClassName} last:border-b-0 bg-white hover:bg-gray-50 transition-colors`}>
                        <div className="text-gray-900 text-sm">{subject}</div>
                        <div className={electiveMinimumLevelCellClassName}>
                          <span className="text-right">{minLevel}</span>
                          <span className={`text-sm font-bold w-5 text-center ${isMatched ? 'text-green-600' : 'text-gray-300'}`}>
                            {isMatched ? '✓' : ' '}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>

      </div>
      </div>
    </div>
  );
};
